import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n, useTranslation } from "next-i18next"
import Head from "next/head"
import Script from "next/script"
import BreadCrumb from "@/components/Default/BreadCrumb"
import { UAParser } from "ua-parser-js"
import AemGrid from "@/components/AemGrid"
import { breadCrumbApplicationJasonLdString } from "@/utils/BreadCrumbApplicationJsonLd"
import CONSTANTS, { footerUrl, headerUrl, publicWebsiteUrl } from "@/constants"
import {
  fetchGlobalConfig,
  fetchPageModel,
  fetchPageShell,
  fetchRequiredAem,
} from "@/services/server/aemPageData.server"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import {
  getClienIP,
  getScriptsAndStyles,
  getAnalyticsScript,
  filterBackEndJson,
  removeAndCleanLink,
  performRedirect,
  generateCanonicalUrl,
  extractScriptsFromDOM,
  getDataLayerObj,
  isCanadaSite,
  getQuickViewAuth,
} from "@/utils/helper"
import { handleServerSideRouteError } from "@/utils/serverSideError"
import { getCartCount, getAriaLabel } from "@/utils/cart"
import {
  selectGenericState,
  setBrandLogoImg,
  closeMenu,
  openMenu,
  setActiveMainCategory,
  setRegionNav,
  setGlobalScripts,
  setGlobalStyles,
  setAuthNav,
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import { selectCartState } from "@/store/features/cartSlice"
import ProductList from "@/components/ProductList/v3/ProductListContext"
import { getPlpParams, formatListingData } from "@/utils/product"
import { setFacetLabels, setPlpData } from "@/store/features/productListSlice"
import {
  getListFromServer,
  getFaucetLabelFromServer,
} from "@/services/server/plp.server"
import useIsSsr from "@/hooks/useIsSsr"
import PLPLoader from "@/components/ProductList/v2/PLPLoader"
import styles from "@/components/ProductList/v2/ProductListView/index.module.scss"
import productTileStyles from "@/components/ProductList/v2/ProductTile/index.module.scss"
import InstallDesignServices from "@/components/InstallDesignServices/v1/InstallDesignServices"
import UpSellInstallServices from "@/components/Shared/UpsellInstallServices"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import DOMPurify from "isomorphic-dompurify"
import {
  getPlpProcessedData,
  reformatFacetString,
  reformatSortString,
} from "@/responseProcessors/plpResponseProcessor"
import { constructGlobalConfigPath } from "@/utils/aemGlobalConfigHelper"
import { selectAuthState } from "@/store/features/authSlice"
import { listingPropTypes } from "@/propTypes/plp"
import { MATCH_DIGIT_FOLLOWED_BY_INCH_SYMBOL_REGEX } from "@/constants/regex"
import akamaiCacheTagNormalization from "@/utils/akamaiCacheTagNormalization"

/**
 * renders home page
 * @return {JSX.Element}
 * @param {object} props The props.
 */
const Listing = props => {
  const {
    tmpData: data,
    headertmp: header,
    footertmp: footer,
    ip,
    adobeAnalytic,
    globalScript,
    footerScript,
    analyticsData,
    datalayerPageName,
    plpData,
    plpBadgeList,
    disableCommerce,
    swatchUrl,
    lang,
    general = {},
    socialMediaConfigs = {},
    plpServletPathArray,
    gbhDataLayerCustom,
    canonicalUrl = "",
    seoPageScript = "",
    breadCrumbApplicationJSonLd = "",
    seoProperties,
    pageData,
    siteName,
    query: { facets: facetQuery, sort: sortQuery },
    templateName = "",
    locale,
    skuPrepend,
    plpPresetConfig,
    productsCount,
    deviceType,
    facetLabelsState,
    bundleProductCardLabel,
  } = props
  const { t } = useTranslation("common")
  const language = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  const isSsr = useIsSsr()
  const { cartUrl } = useSelector(selectCartState)
  const { isAuth } = useSelector(selectAuthState)
  const [pageRedirect, setPageRedirect] = useState("")
  const [seoScripts, setSeoScript] = useState([])
  const { redirectTarget: { page: { path = "" } = {} } = {} } = data
  const {
    brandLogoImg,
    menu: { isOpen, mainCategoryActive, isRegionOpen, isAuthNavOpen },
  } = useSelector(selectGenericState)

  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  usePageAuth()
  const cartCount = getCartCount()
  const ariaLabel = getAriaLabel(t, cartCount)
  useEffect(() => {
    getScriptsAndStyles(footer)
  }, [footer])
  useEffect(() => {
    getAnalyticsScript(adobeAnalytic)
  }, [adobeAnalytic])
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    if (path) {
      setPageRedirect(removeAndCleanLink(path))
    }
  }, [path])
  useEffect(() => {
    if (!isSsr && pageRedirect) {
      router.push(pageRedirect)
    }
  }, [isSsr, pageRedirect])
  useEffect(() => {
    // if (isSsr) return
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    if (plpData) {
      dispatch(
        setPlpData({
          ...plpData,
          status: "succeeded",
          initialLoad: true,
        })
      )
    }
    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom({}))
    }
  }, [datalayerPageName, gbhDataLayerCustom, plpData])
  useEffect(() => {
    dispatch(setFacetLabels(facetLabelsState || {}))
  }, [])
  const pageUrlString = pageData?.pageUrl || publicWebsiteUrl
  const pageUrl = pageUrlString ? new URL(pageUrlString) : null
  const originUrl = pageUrl?.origin ?? publicWebsiteUrl
  const toggleMenu = () => {
    if (isOpen) {
      dispatch(closeMenu())
      dispatch(setAuthNav(false))
    } else {
      dispatch(openMenu())
    }
  }
  const redirect = () => {
    router.push(`${cartUrl}`)
  }
  const eventHandler = {
    store: {
      brandLogoImg: brandLogoImg,
      isOpen,
      mainCategoryActive,
      isRegionOpen,
      isAuthNavOpen,
      cartCount,
      ariaLabel,
      cartUrl,
      plp: {
        ...plpData,
        status: "succeeded",
      },
    },
    callbacks: {
      setBrandLogoImg: path => {
        dispatch(setBrandLogoImg(path))
      },
      closeMenu: () => {
        dispatch(closeMenu())
      },
      setMainCategory: payload => {
        dispatch(setActiveMainCategory(payload))
      },
      toggleMenu: () => {
        toggleMenu()
      },
      setRegionNav: active => {
        dispatch(setRegionNav(active))
      },
      setAuthNav: active => {
        dispatch(setAuthNav(active))
      },
      setGlobalScripts: item => {
        dispatch(setGlobalScripts(item))
      },
      setGlobalStyles: item => {
        dispatch(setGlobalStyles(item))
      },
      onClick: () => {
        redirect()
      },
    },
  }
  useEffect(() => {
    if (seoPageScript) {
      extractSeoPageScript(seoPageScript)
    }
  }, [seoPageScript])
  const extractSeoPageScript = (scriptContent = "") => {
    const scriptList = []
    const doc = new DOMParser().parseFromString(scriptContent, "text/html")
    extractScriptsFromDOM(doc, item => scriptList.push(item))
    setSeoScript(scriptList)
  }
  let imageUrl = ""
  if (templateName === "category-landing-page") {
    imageUrl = pageData?.thumbnailImageUrl
  } else if (originUrl) {
    imageUrl = `${originUrl}/_next/static/media/BLOK_LOGO.00edf835.svg`
  }
  const quickViewAuthData = getQuickViewAuth(footer)
  if (
    typeof window !== "undefined" &&
    window.kohler?.datalayer?.readyState === "ready"
  ) {
    const kohlerDatalayer = window.kohler.datalayer
    if (analyticsData && kohlerDatalayer?.setItem) {
      kohlerDatalayer.setItem("PAGE_INFO", analyticsData)
      if (plpData) {
        kohlerDatalayer.setItem("PLP_LW_DATA", plpData)
      }
    }
  }
  return (
    <>
      <Head>
        {canonicalUrl ? (
          <link
            rel="canonical"
            href={
              language?.i18n?.language === "fr"
                ? canonicalUrl?.replace("/en/", "/fr/")
                : canonicalUrl
            }
          />
        ) : null}
        {breadCrumbApplicationJSonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(breadCrumbApplicationJSonLd),
            }}
          />
        )}
      </Head>
      <>
        <GlobalPageHeader
          themeType={data?.appliedCssClassNames}
          header={header}
          title={data.title}
          globalScripting={globalScript}
          fullUrl={canonicalUrl}
          siteName={siteName}
          phone={seoProperties?.phoneNum}
          imageUrl={imageUrl}
          description={data?.description}
          data={data}
        />
        <style jsx global>
          {`
            @media print {
              @page {
                size: A4 portrait !important;
              }
            }
          `}
        </style>
        {seoScripts.map((script, i) =>
          !script.src && script.innerHTML ? (
            <Script
              key={`seoscript-${i}`}
              id={`seoscript-${i}`}
              type={script.type}
              data-script-purpose="seo"
            >
              {`${script.innerHTML}`}
            </Script>
          ) : null
        )}
        <div id={data.id} className={data.cssClassNames}>
          <AemGrid
            data={data}
            count={0}
            eventHandler={eventHandler}
            breadCrumb={(data, fullData) => (
              <BreadCrumb data={data} fullData={fullData} plpPage={true} />
            )}
            productAccordion={(data, fullData) => (
              <ProductAccordion data={data} fullData={fullData} />
            )}
            productList={(data, eventHandler) => (
              <ProductList
                data={data}
                eventHandler={eventHandler}
                plpData={plpData}
                plpBadgeList={plpBadgeList}
                bundleProductCardLabel={bundleProductCardLabel}
                disableCommerce={disableCommerce}
                swatchUrl={swatchUrl}
                language={lang}
                general={general}
                socialMediaConfigs={socialMediaConfigs}
                plpServletPath={plpServletPathArray}
                isAuth={isAuth}
                facetQuery={facetQuery}
                sortQuery={sortQuery}
                locale={locale}
                skuPrepend={skuPrepend}
                plpPresetConfig={plpPresetConfig}
                productsCount={productsCount}
                deviceType={deviceType}
                analyticsData={analyticsData}
                quickViewAuthData={quickViewAuthData}
              />
            )}
            installDesignServices={data => (
              <InstallDesignServices data={data} />
            )}
            productListFiller={() => (
              <div
                className={`${styles.productListWrapper} ${productTileStyles.productTileWrapper}`}
              >
                <PLPLoader />
              </div>
            )}
            upSellContainer={data => <UpSellInstallServices authData={data} />}
          />
        </div>
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      </>
    </>
  )
}
/**
 *
 * @param {*} context
 * @return {Object}
 */
export async function getServerSideProps({
  req,
  res,
  locale,
  query,
  resolvedUrl,
  params,
}) {
  await i18n?.reloadResources()
  const ua = new UAParser(req.headers["user-agent"])
  const deviceType = ua.getDevice().type || "desktop"
  const userPersona = req.cookies["userPersona"] || "GST"
  const urlPathWithoutParams = req.url?.split("?")[0] ?? `/${locale}`
  const path = constructGlobalConfigPath(
    true,
    `/${locale}${urlPathWithoutParams}`
  )
  if (path.error) {
    return {
      notFound: true,
    }
  }
  let urlPath = req.url.split("?")[0] ?? ""
  if (req.url.indexOf(".json") > -1 && req.url.indexOf("_next") > -1) {
    urlPath = decodeURIComponent(req.url).split("?")[0].split("|").join("/")
    if (urlPath.indexOf("SPLITVAL") > -1) {
      urlPath = urlPath.split("SPLITVAL")[1]
    }
  }
  const { redirectUrl = "" } = performRedirect(urlPath, locale) ?? {}
  const pageModelUrl = `/${locale}${urlPath}.model.json`
  const pageModelRequest = redirectUrl
    ? null
    : fetchPageModel(pageModelUrl).then(
        data => ({ data }),
        error => ({ error })
      )
  /**
   * * Fetch the global configuration data
   * @type {Object}
   * @param {string} path - The path to the global configuration
   */
  let globalConfigData = {}
  try {
    globalConfigData = (await fetchGlobalConfig(path)) || {}
  } catch (error) {
    return handleServerSideRouteError("PLP global config fetch failed", error, {
      url: path,
    })
  }
  const {
    plpConfigurations: { myArrayList: plpBadgeList = [] } = {},
    general = {},
    general: {
      disableCommerce,
      swatchUrl,
      plpServletPath: plpServletApiUrl = "",
    } = {
      swatchUrl: CONSTANTS?.COLOR_FILE_URL,
      disableCommerce: false,
    },
    presetConfigs: { plp: plpPresetConfig = {} } = {},
    internationalization: { lang = "" } = {},
    socialMediaConfig: socialMediaConfigs = {},
    apiEndpoints: { skuPrepend = "" } = {},
    apiEndPointData: {
      map: { canadianPrice, bundleProductCardLabel = "Set" } = {},
    } = {},
  } = globalConfigData
  /* Fetch the PLP servlet path array */
  let plpServletPathArray = []
  try {
    const servletData = await fetchRequiredAem(plpServletApiUrl)
    plpServletPathArray = servletData?.pdpURLMapping || []
  } catch (error) {
    return handleServerSideRouteError("PLP servlet path fetch failed", error, {
      url: plpServletApiUrl,
    })
  }
  let plpData = {}
  const ip = getClienIP(req)

  if (redirectUrl) {
    return {
      redirect: {
        destination: redirectUrl,
        permanent: true,
      },
    }
  }
  const { data, error: pageModelError } = await pageModelRequest
  if (pageModelError) {
    return handleServerSideRouteError(
      "Product list page model fetch failed",
      pageModelError,
      {
        url: pageModelUrl,
        urlPath,
      }
    )
  }
  const { redirectUrl: redirectURL = "", redirectType: redirectStatus = "" } =
    data
  if (redirectURL) {
    return {
      redirect: {
        destination: removeAndCleanLink(redirectURL),
        permanent: redirectStatus === "301",
      },
    }
  }
  const globalScript = data?.globalScript || null
  const footerScript = data?.footerScript || null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const canonicalUrl = generateCanonicalUrl(req?.url, locale, publicWebsiteUrl)
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null
  const seoProperties = data?.seoProperties || ""
  const siteName = data?.siteName
  const isCanadaCheck = isCanadaSite(siteName)
  const templateName = data?.pageComponentProperties?.templateName
  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
    const normalizedCacheTag = akamaiCacheTagNormalization(data?.pageType)
    if (normalizedCacheTag) {
      res.setHeader("Cache-Tag", normalizedCacheTag)
    }
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer?.[
      data?.pageComponentProperties?.id
    ]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer?.[
        data?.pageComponentProperties?.id
      ]
  }
  const categoryKey = getPlpParams(data, "categoryKey")
  const headerModelUrl = `${
    data.headerExpFrg ? data.headerExpFrg : headerUrl
  }.model.json`
  const footerModelUrl = `${
    data.footerExpFrg ? data.footerExpFrg : footerUrl
  }.model.json`
  const { header, footer } = await fetchPageShell({
    headerModelUrl,
    footerModelUrl,
    pageName: "Product list",
    context: {
      urlPath,
    },
  })
  const attributeName = getPlpParams(data, "attributeName")
  const attributevalue = getPlpParams(data, "attributevalue")
  const isServiceParts = getPlpParams(data, "isServiceParts") === "true"
  const plpInitialPayload =
    attributeName && attributevalue
      ? `{!tag="${attributeName}"}${attributeName}:"${attributevalue?.replace(
          MATCH_DIGIT_FOLLOWED_BY_INCH_SYMBOL_REGEX,
          '$1\\"'
        )}"`
      : []

  // if serverside url has values in facets, reformat them
  let facetLabelsState = {}
  try {
    const {
      fusion: { facet_labels: facetlabels = {} },
    } = await getFaucetLabelFromServer(
      categoryKey,
      userPersona,
      siteName,
      locale,
      req
    )
    if (facetlabels) {
      const apiFilterQuery =
        query?.facets !== undefined
          ? reformatFacetString(
              query?.facets,
              facetlabels,
              userPersona,
              isCanadaCheck
            )
          : plpInitialPayload
      const apiSortQuery =
        query?.sort !== undefined
          ? reformatSortString(query.sort, userPersona, isCanadaCheck) ??
            query?.sort
          : ""
      facetLabelsState = {
        ...facetlabels,
      }
      if (apiFilterQuery) {
        const serverData = await getListFromServer(
          categoryKey,
          userPersona,
          siteName,
          locale,
          apiFilterQuery,
          apiSortQuery,
          canadianPrice,
          req,
          isServiceParts
        )
        const formattedData = await formatListingData(serverData, false)

        // Pass all necessary config params to getPlpProcessedData
        const configParams = {
          configdata: {
            plpBadgeList,
            disableCommerce,
            canadianPrice,
          },
          swatchUrl,
          plpServletPath: plpServletPathArray,
          general,
        }
        plpData = await getPlpProcessedData(
          formattedData,
          false,
          [],
          "",
          configParams,
          plpPresetConfig
        )
      }
    }
  } catch (error) {
    return handleServerSideRouteError("Product list data fetch failed", error, {
      categoryKey,
      urlPath,
    })
  }
  const productsCount = 30

  // Enrich analytics data with product count information for Adobe Analytics
  const analyticsData = data?.data

  let tmpData = {}
  const adobeAnalytic = data?.adobeAnalytic || null
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  let bredCrumData
  try {
    bredCrumData = await breadCrumbApplicationJasonLdString(data)
  } catch (error) {
    console.log("Product List Rendering Error:", error)
    return false
  }
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const datalayerPageName = data?.datalayerPageName
  const metaContent = data?.metaContent ?? ""
  const seoPageScript = data?.seoPageScript ?? ""
  const breadCrumbApplicationJSonLd = bredCrumData || null
  const pageData = data?.pageData
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      adobeAnalytic,
      globalScript,
      footerScript,
      topHeadScript,
      analyticsData,
      analyticsScriptClass,
      datalayerPageName,
      plpData,
      plpBadgeList,
      disableCommerce,
      swatchUrl,
      lang,
      general,
      socialMediaConfigs,
      plpServletPathArray,
      gbhDataLayerCustom,
      urlPath,
      canonicalUrl,
      metaContent,
      seoPageScript,
      breadCrumbApplicationJSonLd,
      seoProperties,
      pageData,
      siteName,
      templateName,
      query,
      locale,
      skuPrepend,
      plpPresetConfig,
      productsCount,
      deviceType,
      facetLabelsState,
      bundleProductCardLabel,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default Listing
Listing.propTypes = listingPropTypes
